import { FileTextOutlined } from '@ant-design/icons'
import {
	Button,
	Card,
	Checkbox,
	Form,
	Input,
	Select,
	AutoComplete,
	Space,
	DatePicker,
	message,
} from 'antd'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import api from '../../api/index.api'
import { ListPageIntro, ListPageShell } from '../../common'
import { ROUTE } from '../../utils/constant'
import './BlogCreate.scss'

const slugify = value =>
	String(value || '')
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')

const LOCALE_OPTIONS = [
	{ value: 'en', label: 'English (en)' },
	{ value: 'vi', label: 'Vietnamese (vi)' },
	{ value: 'ja', label: 'Japanese (ja)' },
	{ value: 'ko', label: 'Korean (ko)' },
	{ value: 'zh', label: 'Chinese (zh)' },
	{ value: 'es', label: 'Spanish (es)' },
	{ value: 'fr', label: 'French (fr)' },
]

const CLUSTER_OPTIONS = [
	{ value: 'vocab-tools', label: 'vocab-tools' },
	{ value: 'save-words', label: 'save-words' },
	{ value: 'exam-prep', label: 'exam-prep' },
	{ value: 'brand-proof', label: 'brand-proof' },
]

/** Soft SEO targets (Google SERP). Hard max is higher for draft flexibility. */
const SEO_IDEAL = {
	title: { ideal: 60, max: 200 },
	description: { ideal: 155, max: 400 },
	heading: { ideal: 70, max: 200 },
	excerpt: { ideal: 160, max: 500 },
}

const PUBLISH_REQUIRED_FIELDS = [
	'id',
	'slug',
	'locale',
	'title',
	'description',
	'heading',
	'excerpt',
	'body',
	'cluster',
]

const WIN_NOW_INTENT_OPTIONS = [
	{ value: 'save-words-browsing', label: 'save-words-browsing' },
	{ value: 'double-click-dictionary', label: 'double-click-dictionary' },
	{ value: 'save-youtube-vocab', label: 'save-youtube-vocab' },
	{ value: 'highlight-saved-words', label: 'highlight-saved-words' },
	{ value: 'vocab-extension-vs-anki-dict', label: 'vocab-extension-vs-anki-dict' },
	{ value: 'ielts-vocab-from-reading', label: 'ielts-vocab-from-reading' },
	{ value: 'toeic-vocab-system', label: 'toeic-vocab-system (later)' },
]

const SeoCharHint = ({ value, ideal, hardMax }) => {
	const len = String(value || '').length
	const overIdeal = len > ideal
	const nearIdeal = len >= Math.floor(ideal * 0.85) && !overIdeal
	let tone = 'ok'
	if (overIdeal) tone = 'warn'
	else if (nearIdeal) tone = 'good'
	return (
		<span className={`blog-create__seo-hint blog-create__seo-hint--${tone}`}>
			{len}/{ideal} ideal
			{hardMax ? ` · max ${hardMax}` : ''}
			{overIdeal ? ' — may truncate in SERP' : ''}
		</span>
	)
}

export const BlogCreate = () => {
	const history = useHistory()
	const location = useLocation()
	const [form] = Form.useForm()
	const [saving, setSaving] = useState(false)
	const [loading, setLoading] = useState(false)
	const [seoLens, setSeoLens] = useState({
		title: '',
		description: '',
		heading: '',
		excerpt: '',
	})

	const postPk = useMemo(() => {
		const params = new URLSearchParams(location.search)
		const raw = params.get('id')
		const n = Number(raw)
		return Number.isFinite(n) && n > 0 ? n : null
	}, [location.search])

	const isEdit = postPk != null

	const syncSeoLens = (values = {}) => {
		setSeoLens({
			title: values.title || '',
			description: values.description || '',
			heading: values.heading || '',
			excerpt: values.excerpt || '',
		})
	}

	useEffect(() => {
		if (!postPk) {
			form.setFieldsValue({
				locale: 'en',
				status: 'draft',
				bodyFormat: 'markdown',
				author: 'WordsMine Team',
				noindex: false,
				body: '',
			})
			syncSeoLens({})
			return
		}
		let cancelled = false
		;(async () => {
			setLoading(true)
			try {
				const post = await api.blogAdminApi.getPost(postPk)
				if (cancelled) return
				const next = {
					id: post.id,
					intentId: post.intentId || undefined,
					slug: post.slug,
					locale: post.locale,
					status: post.status,
					title: post.title,
					description: post.description,
					heading: post.heading,
					excerpt: post.excerpt,
					body: post.body,
					bodyFormat: post.bodyFormat || 'markdown',
					readingTime: post.readingTime,
					author: post.author || 'WordsMine Team',
					coverImage: post.coverImage,
					cluster: post.cluster || undefined,
					primaryKeyword: post.primaryKeyword,
					canonicalPath: post.canonicalPath,
					noindex: !!post.noindex,
					publishedAt: post.publishedAt ? moment(post.publishedAt) : null,
					faqText: Array.isArray(post.faq)
						? post.faq
								.map(f => `${f.question}\n${f.answer}`)
								.join('\n---\n')
						: '',
				}
				form.setFieldsValue(next)
				syncSeoLens(next)
			} catch (err) {
				message.error(err?.message || 'Could not load post.')
			} finally {
				if (!cancelled) setLoading(false)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [postPk, form])

	const onTitleBlur = () => {
		const title = form.getFieldValue('title')
		const slug = form.getFieldValue('slug')
		const id = form.getFieldValue('id')
		const heading = form.getFieldValue('heading')
		const description = form.getFieldValue('description')
		const excerpt = form.getFieldValue('excerpt')
		const patch = {}
		if (!slug && title) patch.slug = slugify(title)
		if (!id && title) patch.id = slugify(title)
		if (!heading && title) patch.heading = title
		if (!excerpt && description) patch.excerpt = description
		if (Object.keys(patch).length) {
			form.setFieldsValue(patch)
			syncSeoLens({ ...seoLens, ...patch, title })
		}
	}

	const parseFaq = text => {
		const raw = String(text || '').trim()
		if (!raw) return []
		return raw
			.split(/\n---\n/)
			.map(block => block.trim())
			.filter(Boolean)
			.map(block => {
				const [question, ...rest] = block.split('\n')
				return {
					question: (question || '').trim(),
					answer: rest.join('\n').trim(),
				}
			})
			.filter(f => f.question || f.answer)
	}

	const buildPayload = values => {
		const payload = {
			id: values.id?.trim(),
			intentId: values.intentId?.trim() || '',
			slug: values.slug?.trim(),
			locale: values.locale,
			status: values.status,
			title: values.title?.trim() || '',
			description: values.description?.trim() || '',
			heading: values.heading?.trim() || '',
			excerpt: values.excerpt?.trim() || '',
			body: values.body || '',
			bodyFormat: values.bodyFormat || 'markdown',
			readingTime: values.readingTime?.trim() || '',
			author: values.author?.trim() || 'WordsMine Team',
			coverImage: values.coverImage?.trim() || '',
			cluster: values.cluster || '',
			primaryKeyword: values.primaryKeyword?.trim() || '',
			canonicalPath: values.canonicalPath?.trim() || '',
			noindex: !!values.noindex,
			faq: parseFaq(values.faqText),
		}
		if (values.publishedAt) {
			payload.publishedAt = values.publishedAt.toISOString()
		}
		return payload
	}

	const validatePublishReady = async () => {
		await form.validateFields(['id', 'slug', 'locale', 'title'])
		const values = form.getFieldsValue(true)
		const required = [...PUBLISH_REQUIRED_FIELDS]
		// Strategy §6: intentId required except brand-proof support posts.
		if (values.cluster !== 'brand-proof') {
			required.push('intentId')
		}
		const missing = required.filter(key => !String(values[key] ?? '').trim())
		if (missing.length) {
			form.setFields(
				missing.map(name => ({
					name,
					errors: [`${name} is required to publish`],
				}))
			)
			message.error(`Publish requires: ${missing.join(', ')}`)
			throw new Error('publish validation failed')
		}
		const faq = parseFaq(values.faqText)
		const badFaq = faq.find(f => !f.question || !f.answer)
		if (badFaq) {
			form.setFields([
				{
					name: 'faqText',
					errors: ['Each FAQ block needs both a question and an answer'],
				},
			])
			message.error('Each FAQ block needs both a question and an answer.')
			throw new Error('faq validation failed')
		}
		return values
	}

	const handleSubmit = async values => {
		const status = values.status
		if (status === 'published' || status === 'in_review') {
			try {
				await validatePublishReady()
			} catch (err) {
				if (err?.errorFields) return
				return
			}
		}
		setSaving(true)
		const payload = buildPayload(values)
		if (status === 'published' && !payload.publishedAt) {
			payload.publishedAt = new Date().toISOString()
		}
		try {
			if (isEdit) {
				await api.blogAdminApi.updatePost(postPk, payload)
				message.success('Post updated.')
			} else {
				await api.blogAdminApi.createPost(payload)
				message.success('Post created.')
			}
			history.push(`${ROUTE.BLOG_MANAGEMENT}${ROUTE.LIST}`)
		} catch (err) {
			message.error(err?.message || 'Could not save post.')
		} finally {
			setSaving(false)
		}
	}

	const handlePublish = async () => {
		try {
			const values = await validatePublishReady()
			setSaving(true)
			const payload = buildPayload({ ...values, status: 'published' })
			if (!payload.publishedAt) {
				payload.publishedAt = new Date().toISOString()
			}
			let pk = postPk
			if (isEdit) {
				await api.blogAdminApi.updatePost(postPk, payload)
			} else {
				const created = await api.blogAdminApi.createPost(payload)
				pk = created?.pk
			}
			if (pk) {
				await api.blogAdminApi.publishPost(pk)
			}
			message.success('Post published.')
			history.push(`${ROUTE.BLOG_MANAGEMENT}${ROUTE.LIST}`)
		} catch (err) {
			if (err?.errorFields) {
				message.error('Fill all required SEO fields before publishing.')
				return
			}
			if (err?.message === 'publish validation failed' || err?.message === 'faq validation failed') {
				return
			}
			message.error(err?.message || 'Could not publish post.')
		} finally {
			setSaving(false)
		}
	}

	const handleArchive = async () => {
		if (!isEdit) return
		setSaving(true)
		try {
			await api.blogAdminApi.archivePost(postPk)
			message.success('Post archived.')
			history.push(`${ROUTE.BLOG_MANAGEMENT}${ROUTE.LIST}`)
		} catch (err) {
			message.error(err?.message || 'Could not archive post.')
		} finally {
			setSaving(false)
		}
	}

	return (
		<ListPageShell className="blog-create">
			<ListPageIntro
				icon={FileTextOutlined}
				title={isEdit ? 'Edit blog post' : 'Create blog post'}
				subtitle="Draft freely. Publish requires demand-brief intentId (except brand-proof) + SEO fields."
			/>
			<Card loading={loading} bordered className="blog-create__card">
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
					onValuesChange={(_, all) => syncSeoLens(all)}
				>
					<Space size={16} style={{ display: 'flex' }} wrap>
						<Form.Item
							label="Content id"
							name="id"
							rules={[{ required: true, message: 'id is required' }]}
							style={{ minWidth: 260 }}
							extra="Stable across locales (kebab-case)."
						>
							<Input placeholder="save-words-while-browsing" disabled={isEdit} />
						</Form.Item>
						<Form.Item
							label="Intent id (demand brief)"
							name="intentId"
							style={{ minWidth: 280 }}
							extra="Required to publish (except brand-proof). Must match sheet intent_id."
						>
							<AutoComplete
								allowClear
								options={WIN_NOW_INTENT_OPTIONS}
								placeholder="save-words-browsing"
								filterOption={(input, option) =>
									String(option?.value || '')
										.toLowerCase()
										.includes(String(input || '').toLowerCase())
								}
							/>
						</Form.Item>
						<Form.Item
							label="Slug"
							name="slug"
							rules={[{ required: true, message: 'slug is required' }]}
							style={{ minWidth: 260 }}
						>
							<Input placeholder="save-words-while-browsing" />
						</Form.Item>
						<Form.Item
							label="Locale"
							name="locale"
							rules={[{ required: true }]}
							style={{ minWidth: 180 }}
						>
							<Select options={LOCALE_OPTIONS} />
						</Form.Item>
						<Form.Item
							label="Status"
							name="status"
							rules={[{ required: true }]}
							style={{ minWidth: 160 }}
						>
							<Select
								options={[
									{ value: 'draft', label: 'Draft' },
									{ value: 'in_review', label: 'In review' },
									{ value: 'published', label: 'Published' },
									{ value: 'archived', label: 'Archived' },
								]}
							/>
						</Form.Item>
					</Space>

					<div className="blog-create__seo-block">
						<div className="blog-create__seo-label">SEO essentials</div>
						<Form.Item
							label="Title"
							name="title"
							rules={[{ required: true, message: 'Title is required' }]}
							extra={
								<SeoCharHint
									value={seoLens.title}
									ideal={SEO_IDEAL.title.ideal}
									hardMax={SEO_IDEAL.title.max}
								/>
							}
						>
							<Input
								placeholder="SEO / OG title (≈60 chars)"
								onBlur={onTitleBlur}
								maxLength={SEO_IDEAL.title.max}
								showCount
							/>
						</Form.Item>
						<Form.Item
							label="Heading (H1)"
							name="heading"
							extra={
								<>
									<span className="blog-create__seo-hint-note">Required to publish. </span>
									<SeoCharHint
										value={seoLens.heading}
										ideal={SEO_IDEAL.heading.ideal}
										hardMax={SEO_IDEAL.heading.max}
									/>
								</>
							}
						>
							<Input
								placeholder="Visible H1"
								maxLength={SEO_IDEAL.heading.max}
								showCount
							/>
						</Form.Item>
						<Form.Item
							label="Description (meta)"
							name="description"
							extra={
								<>
									<span className="blog-create__seo-hint-note">Required to publish. </span>
									<SeoCharHint
										value={seoLens.description}
										ideal={SEO_IDEAL.description.ideal}
										hardMax={SEO_IDEAL.description.max}
									/>
								</>
							}
						>
							<Input.TextArea
								rows={2}
								placeholder="Meta description (≈150–160 chars)"
								maxLength={SEO_IDEAL.description.max}
								showCount
							/>
						</Form.Item>
						<Form.Item
							label="Excerpt"
							name="excerpt"
							extra={
								<>
									<span className="blog-create__seo-hint-note">Required to publish. </span>
									<SeoCharHint
										value={seoLens.excerpt}
										ideal={SEO_IDEAL.excerpt.ideal}
										hardMax={SEO_IDEAL.excerpt.max}
									/>
								</>
							}
						>
							<Input.TextArea
								rows={2}
								placeholder="List card / short OG description"
								maxLength={SEO_IDEAL.excerpt.max}
								showCount
							/>
						</Form.Item>
					</div>

					<Space size={16} style={{ display: 'flex' }} wrap>
						<Form.Item
							label="Cluster"
							name="cluster"
							style={{ minWidth: 200 }}
							extra="Required to publish"
						>
							<Select allowClear options={CLUSTER_OPTIONS} placeholder="Topic cluster" />
						</Form.Item>
						<Form.Item label="Body format" name="bodyFormat" style={{ minWidth: 160 }}>
							<Select
								options={[
									{ value: 'markdown', label: 'Markdown' },
									{ value: 'html', label: 'HTML' },
								]}
							/>
						</Form.Item>
						<Form.Item label="Published at" name="publishedAt" style={{ minWidth: 220 }}>
							<DatePicker showTime style={{ width: '100%' }} />
						</Form.Item>
						<Form.Item name="noindex" valuePropName="checked" style={{ marginTop: 30 }}>
							<Checkbox>noindex</Checkbox>
						</Form.Item>
					</Space>

					<Space size={16} style={{ display: 'flex' }} wrap>
						<Form.Item label="Author" name="author" style={{ minWidth: 200 }}>
							<Input />
						</Form.Item>
						<Form.Item label="Reading time" name="readingTime" style={{ minWidth: 200 }}>
							<Input placeholder="5 minute read" />
						</Form.Item>
						<Form.Item label="Primary keyword" name="primaryKeyword" style={{ minWidth: 240 }}>
							<Input placeholder="Tracking aid for GSC / sheets" />
						</Form.Item>
					</Space>

					<Form.Item
						label="Cover image URL"
						name="coverImage"
						extra="Used for OG/social preview when set."
					>
						<Input placeholder="https://... or /blog/..." />
					</Form.Item>
					<Form.Item
						label="Canonical path"
						name="canonicalPath"
						extra="Optional override; default /blog/{slug} or /{locale}/blog/{slug}."
					>
						<Input placeholder="/blog/slug or /vi/blog/slug" />
					</Form.Item>

					<Form.Item
						label="Body"
						name="body"
						extra="Markdown (default) or HTML depending on body format. Required to publish."
					>
						<Input.TextArea rows={14} placeholder={'## Heading\n\nContent...'} />
					</Form.Item>

					<Form.Item
						label="FAQ (AEO)"
						name="faqText"
						extra="Optional. Blocks separated by --- ; first line = question, rest = answer. Mirrored in FAQ JSON-LD on landing."
					>
						<Input.TextArea
							rows={6}
							placeholder={
								'What is WordsMine?\nAnswer here.\n---\nAnother question?\nAnother answer.'
							}
						/>
					</Form.Item>

					<Space wrap>
						<Button type="primary" htmlType="submit" loading={saving}>
							{isEdit ? 'Save' : 'Create draft'}
						</Button>
						<Button loading={saving} onClick={handlePublish}>
							Save & publish
						</Button>
						{isEdit && (
							<Button loading={saving} onClick={handleArchive}>
								Archive
							</Button>
						)}
						<Button
							onClick={() => history.push(`${ROUTE.BLOG_MANAGEMENT}${ROUTE.LIST}`)}
						>
							Cancel
						</Button>
					</Space>
				</Form>
			</Card>
		</ListPageShell>
	)
}
